import { queryString } from '@helpers/utils/strings/strings';
import { z } from 'zod';
import { entitySchema } from './entity-schema';
import { themeTypeSchema } from './kg-insights';

export const companySchema = z.object({
  tag: z.string(),
  name: z.string(),
  ticker: z.string(),
  exchange: z.string(),
  evidence: z.string(),
});
export type Company = z.infer<typeof companySchema>;

export const graphEntityTypeSchema = z.enum([
  'company',
  'theme',
  'country',
  'product',
  'region',
]);
export type GraphEntityType = z.infer<typeof graphEntityTypeSchema>;

export const graphRelationshipTypeSchema = z.enum([
  'company-theme',
  'company-theme-macro',
  'company-theme-financial',
  'company-product',
  'company-country',
  'company-region',
  'company-competitor',
  'theme-company',
  'theme-company-macro',
  'theme-company-financial',
]);
export type GraphRelationshipType = z.infer<typeof graphRelationshipTypeSchema>;

export const graphRelationDirectionSchema = z.enum([
  'inbound',
  'outbound',
  'any',
]);
export type GraphRelationDirection = z.infer<
  typeof graphRelationDirectionSchema
>;

export const relationKindSchema = z.enum([
  'theme-company',
  'company-theme-macro',
  'company-theme-financial',
]);
export type RelationKind = z.infer<typeof relationKindSchema>;

// Node graph id `<type>/<rawId>` (type is one of graphEntityTypeSchema),
// e.g. "company/aapl_nasd"; unique across node types. Split via parseGraphNodeKey.
const graphNodeKeySchema = z.string().regex(/^[^/]+\/.+$/);
// Edge key `<relationKind>/<edge-key>` (kind is one of graphRelationshipTypeSchema),
// e.g. "company-theme/17"; unique across kinds.
const graphEdgeKeySchema = z.string().regex(/^[^/]+\/.+$/);

export const graphNodeAttributesSchema = z.object({
  type: graphEntityTypeSchema,
  exchange: z.string().optional(),
  label: z.string().optional(),
  sub_type: themeTypeSchema.optional(),
  id: z.string().optional(),
  ticker: z.string().optional(),
  icon_url: z.string().optional(),
  logo_urls: entitySchema.shape.logo_urls.optional(),
});
export type GraphNodeAttributesV5 = z.infer<typeof graphNodeAttributesSchema>;

export const graphNodeV5Schema = z.object({
  key: graphNodeKeySchema,
  attributes: graphNodeAttributesSchema,
});
export type GraphNodeV5 = z.infer<typeof graphNodeV5Schema>;

export const edgeAttributesV5Schema = z.object({
  type: graphRelationshipTypeSchema,
  label: z.string().optional(),
  rank: z.number().int(),
  description: z.string().optional(),
  source_evidence: z.string().optional(),
  evidence: z.array(z.string()).optional(),
  weight: z.number().optional(),
  exposure_direction: z.enum(['positive', 'negative', 'neutral']).optional(),
});

export type EdgeAttributesV5 = z.infer<typeof edgeAttributesV5Schema>;

export const graphEdgeV5Schema = z.object({
  key: graphEdgeKeySchema,
  source: graphNodeKeySchema,
  target: graphNodeKeySchema,
  attributes: edgeAttributesV5Schema,
});
export type GraphEdgeV5 = z.infer<typeof graphEdgeV5Schema>;

export const relationsResponseSchema = z.object({
  nodes: z.array(graphNodeV5Schema),
  edges: z.array(graphEdgeV5Schema),
});
export type RelationsResponse = z.infer<typeof relationsResponseSchema>;

export const graphIntersectionEdgeV5Schema = z.object({
  source: graphNodeKeySchema,
  target: graphNodeKeySchema,
  attributes: edgeAttributesV5Schema,
});

const intersectionV5Schema = z.object({
  node: z.string(),
  relationship_description: z.string().optional(),
  edges: z.array(graphIntersectionEdgeV5Schema),
});
export type IntersectionV5 = z.infer<typeof intersectionV5Schema>;

export const intersectionsV5ResponseSchema = z.object({
  nodes: z.array(graphNodeV5Schema),
  intersections: z.array(intersectionV5Schema),
});
export type IntersectionsV5Response = z.infer<
  typeof intersectionsV5ResponseSchema
>;

const intersectionV6Schema = intersectionV5Schema.omit({ edges: true }).extend({
  source_edges: z.array(graphIntersectionEdgeV5Schema).optional(),
  target_edges: z.array(graphIntersectionEdgeV5Schema).optional(),
});

export const intersectionsV6ResponseSchema = z.object({
  nodes: z.array(graphNodeV5Schema),
  intersections: z.array(intersectionV6Schema),
});
export type IntersectionsV6Response = z.infer<
  typeof intersectionsV6ResponseSchema
>;
export type IntersectionV6 = z.infer<typeof intersectionV6Schema>;

export type Intersection = Omit<IntersectionV5, 'edges'> &
  IntersectionV6 & {
    edges?: IntersectionV5['edges'];
  };

export const ontology = {
  getRelations: {
    path: ({
      nodeType,
      node,
      maxRank,
      direction,
      relationKind,
    }: {
      nodeType: GraphEntityType;
      node: string;
      maxRank?: number;
      direction?: GraphRelationDirection;
      relationKind?: RelationKind;
    }) => {
      const search = queryString({
        node_type: nodeType,
        node,
        max_rank: maxRank,
        direction,
        relations: relationKind,
      });
      return `ontology/v5/relations${search}`;
    },
    schema: relationsResponseSchema,
  },
  getRelationsV6: {
    path: ({
      nodeType,
      node,
      maxRank,
      direction,
      relationKind,
    }: {
      nodeType: GraphEntityType;
      node: string;
      maxRank?: number;
      direction?: GraphRelationDirection;
      relationKind?: RelationKind;
    }) => {
      const search = queryString({
        node_type: nodeType,
        node,
        max_rank: maxRank,
        direction,
        relations: relationKind,
      });
      return `ontology/v6/relations${search}`;
    },
    schema: relationsResponseSchema,
  },
  getIntersections: {
    path: ({
      nodeType,
      node,
      limit,
      targetRelation,
    }: {
      nodeType: GraphEntityType;
      node: string;
      targetRelation: string;
      limit?: number;
    }) => {
      const search = queryString({
        node_type: nodeType,
        node,
        target_relation: targetRelation,
        limit,
      });
      return `ontology/v5/intersections${search}`;
    },
    schema: intersectionsV5ResponseSchema,
  },
  getIntersectionsV6: {
    path: ({
      nodeType,
      node,
      targetRelation,
      limit,
    }: {
      nodeType: GraphEntityType;
      node: string;
      targetRelation: string;
      limit?: number;
    }) => {
      const search = queryString({
        node_type: nodeType,
        node,
        target_relation: targetRelation,
        limit,
      });
      return `ontology/v6/intersections${search}`;
    },
    schema: intersectionsV6ResponseSchema,
  },
};
